import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import {
  FaStar,
  FaClock,
  FaComments,
  FaBox,
  FaCreditCard,
  FaCheckCircle,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

/** معايير تقييم السائق (من العميل) */
const getDriverCriteria = (t) => [
  {
    key: "punctuality",
    icon: FaClock,
    title: t("rating.criteria.punctuality.title", "Pünktlichkeit"),
    desc: t(
      "rating.criteria.punctuality.desc",
      "Hat der Fahrer die Abholung und Zustellung pünktlich durchgeführt?"
    ),
  },
  {
    key: "communication",
    icon: FaComments,
    title: t("rating.criteria.communication.title", "Kommunikation"),
    desc: t(
      "rating.criteria.communication.desc",
      "War die Kommunikation einfach und freundlich?"
    ),
  },
  {
    key: "cargo_care",
    icon: FaBox,
    title: t("rating.criteria.cargo_care.title", "Umgang mit der Ware"),
    desc: t(
      "rating.criteria.cargo_care.desc",
      "Wurde Ihre Ware sorgfältig und sicher transportiert?"
    ),
  },
  {
    key: "rating",
    icon: FaStar,
    title: t("rating.criteria.rating.title", "Gesamtbewertung"),
    desc: t(
      "rating.criteria.rating.desc",
      "Wie zufrieden sind Sie insgesamt mit dem Fahrer?"
    ),
  },
];

/** معايير تقييم العميل (من السائق) */
const getCustomerCriteria = (t) => [
  {
    key: "punctuality",
    icon: FaCreditCard,
    title: t("rating.criteria.payment_handling.title", "Zahlungsabwicklung"),
    desc: t(
      "rating.criteria.payment_handling.desc",
      "Wurde die Zahlung wie vereinbart durchgeführt?"
    ),
  },
  {
    key: "communication",
    icon: FaComments,
    title: t("rating.criteria.communication.title", "Kommunikation"),
    desc: t(
      "rating.criteria.communication.desc",
      "War die Kommunikation klar und freundlich?"
    ),
  },
  {
    key: "cargo_care",
    icon: FaBox,
    title: t("rating.criteria.goods_provision.title", "Warenbereitstellung"),
    desc: t(
      "rating.criteria.goods_provision.desc",
      "War die Ware pünktlich und korrekt bereitgestellt?"
    ),
  },
  {
    key: "rating",
    icon: FaStar,
    title: t("rating.criteria.rating.title", "Gesamtbewertung"),
    desc: t(
      "rating.criteria.rating.desc",
      "Wie war Ihre allgemeine Erfahrung mit dem Kunden?"
    ),
  },
];

function StarRow({ value, onChange }) {
  return (
    <div className="d-flex gap-1 flex-shrink-0">
      {[1, 2, 3, 4, 5].map((n) => (
        <FaStar
          key={n}
          size={20}
          style={{ cursor: "pointer" }}
          color={n <= value ? "#f5b301" : "#e4e7ec"}
          onClick={() => onChange(n)}
        />
      ))}
    </div>
  );
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function RatingModal({
  show,
  onHide,
  person, // { name, id, avatar? }
  role = "driver", // "driver" = تقييم السائق | "customer" = تقييم العميل
  onSubmit,
  submitting = false,
}) {
  const { t } = useTranslation();

  const [scores, setScores] = useState({
    punctuality: 0,
    communication: 0,
    cargo_care: 0,
    rating: 0,
  });
  const [comment, setComment] = useState("");

  // إعادة تعيين عند الفتح/الإغلاق
  useEffect(() => {
    if (!show) {
      setScores({
        punctuality: 0,
        communication: 0,
        cargo_care: 0,
        rating: 0,
      });
      setComment("");
    }
  }, [show]);

  const setScore = (key, val) =>
    setScores((prev) => ({ ...prev, [key]: val }));

  const allFilled =
    scores.punctuality > 0 &&
    scores.communication > 0 &&
    scores.cargo_care > 0 &&
    scores.rating > 0;

  const handleSubmit = () => {
    if (!allFilled || submitting) return;
    onSubmit({
      punctuality: scores.punctuality,
      communication: scores.communication,
      cargo_care: scores.cargo_care,
      rating: scores.rating,
      comment: comment.trim() || null,
    });
  };

  const handleClose = () => {
    onHide();
  };

  const isDriver = role === "driver";
  const criteria = isDriver ? getDriverCriteria(t) : getCustomerCriteria(t);

    const title = isDriver
    ? t("rating.title_driver", "Bewerten Sie den Fahrer")
    : t("rating.title_customer", "Bewerten Sie den Kunden");

    // في subtitle
    const subtitle = isDriver
    ? t("rating.subtitle_driver", "Ihr Feedback hilft uns...")
    : t("rating.subtitle_customer", "Ihre Bewertung hilft uns...");

    // في idLabel
    const idLabel = isDriver 
    ? t("rating.driver_id", "Fahrer-ID") 
    : t("rating.customer_id", "Kunden-ID");

  return (
    <Modal show={show} onHide={handleClose} centered size="md">
      <Modal.Body className="p-4 position-relative">
        <button
          type="button"
          className="btn-close position-absolute"
          style={{ top: 16, right: 16 }}
          onClick={handleClose}
          aria-label="Close"
        />

        <h4 className="fw-bold mb-1 pe-4" style={{ color: "#101828" }}>
          {title}
        </h4>
        <p className="text-muted small mb-3">{subtitle}</p>

        {/* بطاقة الشخص */}
        {person && (
          <div
            className="d-flex align-items-center gap-3 mb-3 p-3 rounded-3"
            style={{ background: "#f0fdf9" }}
          >
            {person.avatar ? (
              <img
                src={person.avatar}
                alt=""
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                style={{
                  width: 48,
                  height: 48,
                  background: "#5c7c5e",
                  fontSize: 16,
                }}
              >
                {getInitials(person.name)}
              </div>
            )}
            <div>
              <div className="fw-bold" style={{ color: "#101828" }}>
                {person.name || "—"}
              </div>
              <small className="text-muted">
                {idLabel}: #{person.id ?? "—"}
              </small>
            </div>
          </div>
        )}

        {/* المعايير */}
        <div className="d-flex flex-column gap-2 mb-3">
          {criteria.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.key}
                className="d-flex align-items-center gap-3 p-3 rounded-3 border"
                style={{ background: "#fff" }}
              >
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 40,
                    height: 40,
                    background: "#f2f4f7",
                  }}
                >
                  <Icon size={18} color="#667085" />
                </div>
                <div className="flex-grow-1 min-width-0">
                  <div className="fw-semibold" style={{ fontSize: 14 }}>
                    {c.title}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {c.desc}
                  </div>
                </div>
                <StarRow
                  value={scores[c.key]}
                  onChange={(v) => setScore(c.key, v)}
                />
              </div>
            );
          })}
        </div>

        {/* تعليق اختياري */}
        <Form.Group className="mb-3">
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={500}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={
            isDriver
                ? t("rating.comment_placeholder_driver", "Weitere Anmerkungen (optional)...")
                : t("rating.comment_placeholder_customer", "Weitere Anmerkungen (optional)")
            }
            style={{ resize: "none" }}
          />
          <div className="text-end text-muted small mt-1">
            {comment.length}/500
          </div>
        </Form.Group>

        <Button
          className="w-100 py-2 fw-bold border-0 d-flex align-items-center justify-content-center gap-2"
          style={{
            background: allFilled ? "#0d9f6e" : "#98a2b3",
            borderRadius: 12,
          }}
          disabled={!allFilled || submitting}
          onClick={handleSubmit}
        >
          {submitting ? (
            <Spinner size="sm" animation="border" />
          ) : (
            <>
               {t("rating.submit_button", "Bewertung abgeben")}
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: 22,
                  height: 22,
                  background: "rgba(255,255,255,0.25)",
                  fontSize: 12,
                }}
              >
                <FaCheckCircle size={14} />
              </span>
            </>
          )}
        </Button>
      </Modal.Body>
    </Modal>
  );
}